import { Box, Grid, Pagination, PaginationItem, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { getAccessibleBadgeColors } from "../utils/contrast";
import NewsItemLateral from "./commons/NewsItemLateral";
import SmartImage from "./commons/SmartImage";

import { Link, useSearchParams } from "react-router-dom";
import NewsItem from "./commons/NewsItem";
import { ArrowBackOutlined, ArrowForwardOutlined } from "@mui/icons-material";
import { fetchList, isAllCategories, newsImage, pagesFor, resultsLabel } from "../utils/api";

const LIMIT = 30;

function AllNews({ isMobile, legal }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [news, setNews] = useState([]);
    const [total, setTotal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const page = Number(searchParams.get("page")) || 1;
    const search = searchParams.get("search") || "";
    const categorie = searchParams.get("categorie") || "";

    const activeCategorie = legal ? "JURÍDICO" : isAllCategories(categorie) ? "" : categorie;
    const totalPages = loading && total === null
        ? page
        : pagesFor({ total, itemsLength: news.length, page, limit: LIMIT });

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);

        fetchList("news", {
            title: search,
            categorie: activeCategorie,
            limit: LIMIT,
            page: page - 1,
            slim: 1,
        })
            .then(({ items, total }) => {
                if (cancelled) return;
                setNews(items);
                setTotal(total);
            })
            .catch(() => {
                if (cancelled) return;
                setNews([]);
                setTotal(0);
                setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        window.scrollTo({ top: 0, behavior: "smooth" });

        return () => {
            cancelled = true;
        };
    }, [page, search, activeCategorie]);

    function handlePageChange(_, number) {
        const next = { page: number };
        if (search) next.search = search;
        if (categorie) next.categorie = categorie;
        setSearchParams(next);
    }

    function formatDate(date) {
        const months = [
            "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
            "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
        ];
        const newDate = new Date(date);
        return `${newDate.getDate()} de ${months[newDate.getMonth()]} de ${newDate.getFullYear()}`;
    }

    const heading = legal
        ? "Jurídico"
        : activeCategorie
            ? activeCategorie
            : search
                ? "Resultados da busca"
                : "Todas as notícias";

    const subtitle = (() => {
        if (loading) return "Carregando...";
        if (error) return "Não foi possível carregar as notícias. Tente novamente em instantes.";
        if (total === null) return search ? `Resultados para "${search}"` : "";
        const label = resultsLabel(total, "notícia", "notícias");
        return search ? `${label} para "${search}"` : label;
    })();

    return (
        <Fragment>
            {legal && isMobile && (
                <a
                    href="https://infosind.com.br/Juridico/Login/%7B74248E80-6235-442F-884F-FC1323853621%7D"
                    target={"_blank"}
                    rel="noreferrer"
                    style={{
                        paddingLeft: isMobile ? 16 : 64,
                        paddingTop: isMobile ? 0 : 12,
                        textDecoration: "none",
                    }}
                >
                    <img
                        src={"../files/bannerprocessos.png"}
                        width={isMobile ? "90%" : "720px"}
                        alt="banner de processos"
                    />
                </a>
            )}

            <Grid container marginY={6} gap={2} display={"flex"} paddingX={isMobile ? 2 : 8}>
                <Grid xs={12} md={12} marginTop={4} color="#010101" borderBottom={1}>
                    <Typography
                        component="h2"
                        color="#010101"
                        width={"fit-content"}
                        borderBottom={1}
                        fontFamily="Raleway"
                        fontStyle="normal"
                        fontWeight="800"
                        fontSize="20px"
                        lineHeight="23px"
                    >
                        {heading}
                    </Typography>
                    <Typography
                        role="status"
                        aria-live="polite"
                        color="#4A4A4A"
                        fontFamily="Raleway"
                        fontSize="14px"
                        lineHeight="20px"
                        marginTop={1}
                        marginBottom={1}
                    >
                        {subtitle}
                    </Typography>
                </Grid>

                {!loading && !error && news.length === 0 && (
                    <Grid xs={12} md={12} padding={"16px 10px"}>
                        <Typography color="#010101" fontFamily="Raleway" fontSize="16px" lineHeight="22px">
                            Nenhuma notícia encontrada
                            {search ? ` para "${search}"` : ""}
                            {activeCategorie && !legal ? ` na categoria ${activeCategorie}` : ""}.
                            {" "}
                            <Link to={legal ? "/juridico" : "/noticias"} style={{ color: "#C81E1E" }}>
                                Ver todas as notícias
                            </Link>
                        </Typography>
                    </Grid>
                )}

                {news.length >= 3 && (
                    <Box style={{ width: '100vw', display: 'flex', gap: 16 }}>
                        <Grid xs={12} md={5.5} display={"flex"} style={{ maxWidth: "600px" }} flexDirection={"column"}>
                            <Link to={`/noticia?link=${news[0].link}`} rel="noreferrer" style={{ textDecoration: "none" }}>
                                <SmartImage
                                    image={newsImage(news[0])}
                                    style={{ width: "100%", maxWidth: "600px", maxHeight: "400px", objectFit: "fill", display: "block" }}
                                    alt={news[0].titulo}
                                />
                                <Grid container display={"flex"} gap={2} mt={1}>
                                    <Grid xs={6} md={"auto"}>
                                        <Typography color="#010101" fontFamily="Raleway" fontStyle="normal" fontWeight="400" fontSize="12px" lineHeight="14px">
                                            {formatDate(news[0].incData)}
                                        </Typography>
                                    </Grid>
                                    <Grid xs={"auto"} md={"auto"} style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "2px 12px", borderRadius: 50, ...getAccessibleBadgeColors(news[0].categorieColor) }}>
                                        <Typography fontFamily="Raleway" fontStyle="normal" fontWeight="700" fontSize="12px" lineHeight="16px" letterSpacing={"0.1em"}>
                                            {news[0].categorie}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Typography color="#010101" fontFamily="Raleway" fontStyle="normal" fontWeight="700" fontSize="24px" lineHeight="28px" marginTop={2}>
                                    {news[0].titulo}
                                </Typography>
                            </Link>
                        </Grid>

                        <Grid xs={12} md={2.8} display={"flex"} flexDirection={"column"} borderLeft={1} borderRight={1} padding={"0px 8px"} alignItems={"center"}>
                            {news[1] && (
                                <NewsItem
                                    image={newsImage(news[1])}
                                    date={formatDate(news[1].incData)}
                                    description={news[1].titulo}
                                    link={news[1].link}
                                    categorie={news[1].categorie}
                                    categorieColor={news[1].categorieColor}
                                />
                            )}
                            {news[2] && (
                                <NewsItem
                                    image={newsImage(news[2])}
                                    date={formatDate(news[2].incData)}
                                    description={news[2].titulo}
                                    link={news[2].link}
                                    categorie={news[2].categorie}
                                    categorieColor={news[2].categorieColor}
                                />
                            )}
                        </Grid>

                        <Grid xs={12} md={2.8} display={"flex"} flexDirection={"column"} gap={2}>
                            {news.slice(3, 10).map((item) => (
                                <Box borderBottom={1} paddingBottom={2} key={item._id}>
                                    <Link to={`/noticia?link=${item.link}`} rel="noreferrer" style={{ textDecoration: "none" }}>
                                        <Grid container display={"flex"}>
                                            <Grid xs={"auto"} md={"auto"}>
                                                <Typography color="#010101" fontFamily="Raleway" fontStyle="normal" fontWeight="400" fontSize="12px" lineHeight="14px">
                                                    {formatDate(item.incData)}
                                                </Typography>
                                            </Grid>
                                            <Grid xs={"auto"} md={"auto"} style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "2px 12px", borderRadius: 50, marginLeft: 8, ...getAccessibleBadgeColors(item.categorieColor) }}>
                                                <Typography fontFamily="Raleway" fontStyle="normal" fontWeight="700" fontSize="12px" lineHeight="16px" letterSpacing={"0.1em"}>
                                                    {item.categorie}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Typography color="#010101" fontFamily="Raleway" fontStyle="normal" fontWeight="600" fontSize="14px" lineHeight="16px" marginTop={1}>
                                            {item.titulo}
                                        </Typography>
                                    </Link>
                                </Box>
                            ))}
                        </Grid>
                    </Box>
                )}

                {news.length > 0 && news.length < 3 && (
                    <Grid container gap={2} display={"flex"}>
                        {news.map((item) => (
                            <Grid xs={12} md={4.5} display={"flex"} padding={"0px 10px"} key={item._id}>
                                <NewsItemLateral
                                    image={newsImage(item)}
                                    date={item.incData}
                                    title={item.titulo}
                                    description={item.description}
                                    link={item.link}
                                    categorie={item.categorie}
                                    categorieColor={item.categorieColor}
                                    isMobile={isMobile}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Grid xs={12} md={12} display={"flex"} padding={"0px 10px"}>
                    <Grid container marginY={6} gap={2} display={"flex"} position={"relative"}>
                        {news.slice(10).map((item) => (
                            <Grid xs={12} md={4.5} display={"flex"} padding={"0px 10px"} key={item._id}>
                                <NewsItemLateral
                                    image={newsImage(item)}
                                    date={item.incData}
                                    title={item.titulo}
                                    description={item.description}
                                    link={item.link}
                                    categorie={item.categorie}
                                    categorieColor={item.categorieColor}
                                    isMobile={isMobile}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <Grid xs={12} md={2.8} display={"flex"} padding={"0px 10px"} position={"fixed"} right={2} marginTop={4}>
                        {legal && !isMobile && (
                            <a
                                href="https://infosind.com.br/Juridico/Login/%7B74248E80-6235-442F-884F-FC1323853621%7D"
                                target={"_blank"}
                                rel="noreferrer"
                                style={{ paddingLeft: 8, textDecoration: "none" }}
                            >
                                <img src={"../files/bannerprocessos.png"} width={"90%"} alt="banner de processos" />
                            </a>
                        )}
                    </Grid>
                </Grid>
            </Grid>

            {totalPages > 1 && (
                <Grid xs={12} md={12} padding={"16px 0px"} color="#FFFFFF" borderBottom={1} display={"flex"} justifyContent={"center"}>
                    <Pagination
                        color="warning"
                        variant="outlined"
                        count={totalPages}
                        page={Math.min(page, totalPages)}
                        onChange={handlePageChange}
                        showFirstButton
                        showLastButton
                        siblingCount={isMobile ? 0 : 1}
                        renderItem={(item) => (
                            <PaginationItem
                                style={{
                                    borderRadius: 100,
                                    color: "#C81E1E",
                                    padding: "8px 16px",
                                    fontFamily: "Raleway",
                                    fontStyle: "normal",
                                    fontWeight: "600",
                                    fontSize: "16px",
                                    lineHeight: "20px",
                                }}
                                slots={{ previous: ArrowBackOutlined, next: ArrowForwardOutlined }}
                                {...item}
                            />
                        )}
                    />
                </Grid>
            )}
        </Fragment>
    );
}

export default AllNews;
